import {
    useContext,
    useEffect,
    type FC,
    useRef
} from "react";
import {
    Pressable,
    Platform,
    View
} from "react-native";
import type IAccordionTriggerProps from "./type";
import type {
    AccordionTriggerKeyboardEventType,
    AccordionTriggerHeadingPropsType,
    AccordionTriggerWebPropsType
} from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useInteractionState
} from "../../../../helpers/interaction";
import {
    usePointerType
} from "../../../../helpers/pointer";
import type {
    AccordionItemPartContentProps
} from "../../type";
import {
    getIsAccordionAttributeNode,
    ACCORDION_NAVIGATION_KEYS
} from "../../util";
import {
    withAccordionItemContext,
    AccordionLayoutContext
} from "../../context";
import {
    getAccordionMeasures,
    getAccordionColors
} from "../../stylesheet";
import DisclosureChevron from "../../../collapsible/internal/disclosureChevron";
import Text from "../../../text";

const AccordionTriggerContent: FC<AccordionItemPartContentProps<IAccordionTriggerProps>> = ({
    customTheme: customThemeProp,
    headingLevel = 3,
    icon: IconProp,
    rightContent,
    itemContext,
    onHoverOut,
    onPressOut,
    onHoverIn,
    onPressIn,
    subTitle,
    context,
    onFocus,
    onBlur,
    style,
    title,
    ...props
}) => {
    const customTheme = customThemeProp ?? context.customTheme;

    const {
        controlPaddings,
        controlHeights,
        shapeConfigs,
        touchTargets,
        typography,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const pointerType = usePointerType();

    const triggerRef = useRef<View>(null);

    const layout = useContext(AccordionLayoutContext);

    const {
        isDisabled
    } = itemContext;

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState({
        isDisabled,
        onHoverOut,
        onPressOut,
        onHoverIn,
        onPressIn,
        onFocus,
        onBlur
    });

    const currentMeasures = getAccordionMeasures({
        variant: context.variant,
        size: context.size,
        controlPaddings,
        controlHeights,
        shapeConfigs,
        typography,
        iconSizes,
        spaces
    });

    const currentColors = getAccordionColors({
        isDisabled,
        colors
    });

    const {
        container: containerDynamicStyle,
        textBlock: textBlockDynamicStyle,
        chevron: chevronDynamicStyle,
        content: contentDynamicStyle,
        layer: layerDynamicStyle,
        icon: iconDynamicStyle,
        meta: metaDynamicStyle
    } = useStyles({
        isHovered: isHovered && !itemContext.isLocked,
        isPressed: isPressed && !itemContext.isLocked,
        isOpen: itemContext.isOpen,
        variant: context.variant,
        currentMeasures,
        isFocusVisible,
        shapeConfigs,
        touchTargets,
        pointerType,
        isDisabled,
        radiuses,
        borders,
        colors,
        layout,
        spaces
    });

    useEffect(() => {
        return context.registerTrigger(itemContext.triggerID, {
            ref: triggerRef,
            isDisabled
        });
    }, [
        context.registerTrigger,
        itemContext.triggerID,
        isDisabled
    ]);

    useEffect(() => {
        const triggerNode: unknown = triggerRef.current;

        if(Platform.OS !== "web" || isDisabled || !getIsAccordionAttributeNode(triggerNode)) {
            return;
        }

        if(itemContext.isLocked) {
            triggerNode.setAttribute("aria-disabled", "true");

            return;
        }

        triggerNode.removeAttribute("aria-disabled");
    }, [
        itemContext.isLocked,
        isDisabled
    ]);

    const handlePress = () => {
        if(isDisabled) {
            return;
        }

        context.handleToggle(itemContext.value);
    };

    const onKeyDown = (event: AccordionTriggerKeyboardEventType) => {
        if(!ACCORDION_NAVIGATION_KEYS.includes(event.key)) {
            return;
        }

        const isNavigated = context.handleNavigate({
            id: itemContext.triggerID,
            key: event.key
        });

        if(isNavigated) {
            event.preventDefault();
        }
    };

    const headingProps: AccordionTriggerHeadingPropsType = Platform.OS === "web" ? {
        "aria-level": headingLevel
    } : {};

    const webProps: AccordionTriggerWebPropsType = Platform.OS === "web" ? {
        "aria-controls": itemContext.contentID,
        onKeyDown
    } : {};

    const renderIcon = () => {
        if(!IconProp) {
            return null;
        }

        return <View
            style={[
                stylesheet.icon,
                iconDynamicStyle
            ]}
            aria-hidden={true}
        >
            <IconProp
                customColor={currentColors.iconColor}
                color={currentColors.iconColorKey}
                size={currentMeasures.iconSize}
            />
        </View>;
    };

    const renderSubTitle = () => {
        if(!subTitle) {
            return null;
        }

        return <Text
            variant={currentMeasures.subTitleVariant}
            customColor={currentColors.subTitleColor}
            customTheme={customTheme}
        >
            {subTitle}
        </Text>;
    };

    const renderMeta = () => {
        if(!rightContent && rightContent !== 0) {
            return null;
        }

        return <View
            style={[
                stylesheet.meta,
                metaDynamicStyle
            ]}
        >
            {
                typeof rightContent === "string" || typeof rightContent === "number" ?
                    <Text
                        variant={currentMeasures.subTitleVariant}
                        customColor={currentColors.metaColor}
                        customTheme={customTheme}
                    >
                        {rightContent}
                    </Text>
                :
                    rightContent
            }
        </View>;
    };

    return <View
        {...headingProps}
        style={stylesheet.header}
        role="heading"
    >
        <Pressable
            {...props}
            {...webProps}
            {...interactionHandlers}
            aria-disabled={isDisabled || itemContext.isLocked}
            aria-expanded={itemContext.isOpen}
            disabled={isDisabled || undefined}
            id={itemContext.triggerID}
            style={[
                style,
                stylesheet.container,
                containerDynamicStyle
            ]}
            focusable={!isDisabled}
            onPress={handlePress}
            ref={triggerRef}
            role="button"
        >
            <View
                style={[
                    stylesheet.layer,
                    layerDynamicStyle
                ]}
                aria-hidden={true}
            />
            <View
                style={[
                    stylesheet.content,
                    contentDynamicStyle
                ]}
            >
                {renderIcon()}
                <View
                    style={[
                        stylesheet.textBlock,
                        textBlockDynamicStyle
                    ]}
                >
                    <Text
                        customColor={currentColors.titleColor}
                        variant={currentMeasures.labelVariant}
                        customTheme={customTheme}
                    >
                        {title}
                    </Text>
                    {renderSubTitle()}
                </View>
                {renderMeta()}
                <DisclosureChevron
                    color={currentColors.chevronColor}
                    size={currentMeasures.iconSize}
                    isOpen={itemContext.isOpen}
                    style={chevronDynamicStyle}
                    customTheme={customTheme}
                />
            </View>
        </Pressable>
    </View>;
};
export default withAccordionItemContext("trigger", AccordionTriggerContent);
